import { useEffect, useState, type ReactNode } from 'react';

import { t } from './i18n';

/**
 * One height for everything that sits on a row.
 *
 * Buttons, inputs, selects and segmented groups used to pick their own padding, so any
 * toolbar was a row of four slightly different rectangles on four different baselines.
 * A shared height is most of what separates a designed console from an assembled one.
 */
export const CONTROL = 'h-[30px]';

/**
 * Ink for ordinary actions, rust for the one that changes what visitors see.
 *
 * "publish" is its own variant rather than a prop on primary, because it is the only
 * irreversible action in the console and should not be one word away from "save".
 *
 * Solid fills are for actions only. View toggles used to be solid ink too, which put the
 * heaviest object on the screen on the least important control — see Segmented.
 */
export function Button({
  children, onClick, href, variant = 'primary', disabled, type = 'button', title, size = 'md',
}: {
  children: ReactNode;
  onClick?: () => void;
  /** Renders an anchor instead. Routing here is the hash, so a link needs no router. */
  href?: string;
  variant?: 'primary' | 'publish' | 'secondary' | 'danger' | 'ghost';
  disabled?: boolean;
  type?: 'button' | 'submit';
  title?: string;
  size?: 'sm' | 'md';
}) {
  const base =
    'inline-flex shrink-0 items-center justify-center gap-1.5 rounded-[3px] font-medium ' +
    'whitespace-nowrap transition-colors disabled:cursor-not-allowed ' +
    (size === 'sm' ? 'h-[26px] px-2.5 text-xs ' : `${CONTROL} px-3.5 text-[13px] `);
  // A disabled solid button used to render as a mid-grey block, which reads as an ordinary
  // secondary button rather than as something switched off — the "Einladen" button looked
  // like the main action on a form nobody had filled in yet. Disabled now goes flat.
  const off =
    'disabled:border disabled:border-[var(--color-line)] disabled:bg-transparent ' +
    'disabled:text-[var(--color-muted)]/70 disabled:shadow-none';
  const styles = {
    primary: `bg-[var(--color-ink)] text-[var(--color-surface)] hover:bg-[#33302a] ${off}`,
    publish: `bg-[var(--color-brand)] text-[var(--color-surface)] hover:bg-[var(--color-brand-ink)] ${off}`,
    secondary:
      'border border-[var(--color-line)] bg-[var(--color-surface)] hover:border-[var(--color-muted)]/40 ' +
      `hover:bg-[var(--color-sunken)] ${off}`,
    danger: `border border-[var(--color-danger)]/35 text-[var(--color-danger)] hover:bg-[var(--color-danger)]/8 ${off}`,
    ghost: 'text-[var(--color-muted)] hover:bg-[var(--color-sunken)] hover:text-[var(--color-ink)] disabled:opacity-40',
  }[variant];
  const cls = `${base} ${styles}`;
  // An anchor rather than a button with a navigate() call: it keeps middle-click and
  // "open in new tab" working, and it spares this component an import of the router.
  return href ? (
    <a href={href} title={title} className={cls}>{children}</a>
  ) : (
    <button type={type} onClick={onClick} disabled={disabled} title={title} className={cls}>
      {children}
    </button>
  );
}

/**
 * A square button whose whole label is a glyph.
 *
 * It carries `title` and `aria-label` as one required prop, because an icon-only control
 * without a name is a control only its author can use.
 */
export function IconButton({ icon, label, onClick, disabled }: {
  icon: IconName; label: string; onClick?: () => void; disabled?: boolean;
}) {
  return (
    <button
      type="button" onClick={onClick} disabled={disabled} title={label} aria-label={label}
      className={`grid ${CONTROL} w-[30px] place-items-center rounded-[3px] text-[var(--color-muted)]
                  transition-colors hover:bg-[var(--color-sunken)] hover:text-[var(--color-ink)]
                  disabled:cursor-not-allowed disabled:text-[var(--color-line)] disabled:hover:bg-transparent`}
    >
      <Icon name={icon} />
    </button>
  );
}

/**
 * One switch between a handful of views.
 *
 * There were four hand-rolled versions of this — date range, device, preview view, and the
 * banner/preferences toggle — and three of them marked the active option with a solid
 * black rectangle. That made a *view toggle* the heaviest thing on the page, louder than
 * publishing. Here the track recedes and the active option is a raised chip.
 */
export function Segmented<T extends string>({ value, options, onChange, size = 'md' }: {
  value: T;
  options: { value: T; label: string; icon?: IconName; title?: string }[];
  onChange: (v: T) => void;
  size?: 'sm' | 'md';
}) {
  const h = size === 'sm' ? 'h-[26px]' : CONTROL;
  return (
    <div className={`inline-flex ${h} items-stretch gap-0.5 rounded-[4px] border border-[var(--color-line)]
                     bg-[var(--color-sunken)] p-[2px]`} role="group">
      {options.map((o) => {
        const on = o.value === value;
        return (
          <button
            key={o.value}
            type="button"
            onClick={() => onChange(o.value)}
            title={o.title ?? o.label}
            aria-label={o.title ?? o.label}
            aria-pressed={on}
            className={`inline-flex items-center gap-1.5 rounded-[2px] px-2.5 text-xs font-medium
                        transition-colors ${
              on
                ? 'bg-[var(--color-surface)] text-[var(--color-ink)] shadow-[0_1px_1px_rgba(25,23,19,.10)]'
                : 'text-[var(--color-muted)] hover:text-[var(--color-ink)]'
            }`}
          >
            {o.icon && <Icon name={o.icon} />}
            {o.icon ? <span className="sr-only">{o.label}</span> : o.label}
          </button>
        );
      })}
    </div>
  );
}

/**
 * A state, never an action.
 *
 * "live" and "nie veröffentlicht" were pill-shaped in one place and bare text in another,
 * and the role badge was monospace snake_case. One shape, so a badge is always readable as
 * a fact about the row rather than as something to press.
 */
export function Badge({ tone = 'neutral', children, title }: {
  tone?: 'neutral' | 'live' | 'note' | 'danger';
  children: ReactNode;
  title?: string;
}) {
  const styles = {
    neutral: 'border-[var(--color-line)] bg-[var(--color-paper)] text-[var(--color-muted)]',
    live: 'border-[var(--color-live)]/25 bg-[var(--color-live)]/8 text-[var(--color-live)]',
    note: 'border-[var(--color-note-line)] bg-[var(--color-note)] text-[var(--color-note-ink)]',
    danger: 'border-[var(--color-danger)]/25 bg-[var(--color-danger)]/8 text-[var(--color-danger)]',
  }[tone];
  return (
    <span title={title}
      className={`inline-flex shrink-0 items-center gap-1 rounded-[3px] border px-1.5 py-px
                  text-[11px] font-medium whitespace-nowrap ${styles}`}>
      {children}
    </span>
  );
}

export type IconName =
  | 'undo' | 'redo' | 'desktop' | 'tablet' | 'mobile' | 'check' | 'plus' | 'external'
  | 'search' | 'up' | 'down' | 'help';

/**
 * Inline, stroked, all on the same 16-unit grid.
 *
 * The toolbar previously mixed a text arrow (↶), a geometric shape (▯) and an emoji (🖥):
 * three different rendering paths, three different weights, and on some systems a colour
 * emoji sitting in a monochrome row.
 */
export function Icon({ name, size = 15 }: { name: IconName; size?: number }) {
  const paths: Record<IconName, ReactNode> = {
    undo: <><path d="M6.2 4.2 2.7 7.7l3.5 3.5" /><path d="M2.7 7.7h6.6a3.5 3.5 0 1 1 0 7H7" /></>,
    redo: <><path d="M9.8 4.2l3.5 3.5-3.5 3.5" /><path d="M13.3 7.7H6.7a3.5 3.5 0 1 0 0 7H9" /></>,
    desktop: <><rect x="1.6" y="2.6" width="12.8" height="8.6" rx="1.2" /><path d="M8 11.2v2.2M5.5 13.4h5" /></>,
    tablet: <><rect x="3.4" y="1.6" width="9.2" height="12.8" rx="1.4" /><path d="M8 12.2h.01" /></>,
    mobile: <><rect x="4.9" y="1.4" width="6.2" height="13.2" rx="1.4" /><path d="M8 12.4h.01" /></>,
    check: <path d="M3.2 8.4l3.2 3.2 6.4-7" />,
    plus: <path d="M8 3.4v9.2M3.4 8h9.2" />,
    external: <><path d="M9.2 2.8h4v4" /><path d="M13.2 2.8 7.4 8.6" /><path d="M11.6 9.6v3.2a.8.8 0 0 1-.8.8H3.2a.8.8 0 0 1-.8-.8V5.2a.8.8 0 0 1 .8-.8h3.2" /></>,
    search: <><circle cx="7.2" cy="7.2" r="4.4" /><path d="M10.4 10.4 13.6 13.6" /></>,
    up: <path d="M4.4 9.6 8 6l3.6 3.6" />,
    down: <path d="M4.4 6.4 8 10l3.6-3.6" />,
    // Drawn rather than typed: a "?" in a circle made of text sits on the text baseline and
    // changes size with the surrounding font, so it never lined up with the heading it
    // belonged to.
    help: <><circle cx="8" cy="8" r="6.2" /><path d="M6.2 6.2a1.9 1.9 0 1 1 2.4 1.9c-.4.15-.6.5-.6.9v.4" /><path d="M8 12.1h.01" /></>,
  };
  return (
    <svg width={size} height={size} viewBox="0 0 16 16" aria-hidden="true" focusable="false"
      fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"
      strokeLinejoin="round" className="shrink-0">
      {paths[name]}
    </svg>
  );
}

export function Field({ label, hint, error, children }: {
  label: string; hint?: string; error?: string; children: ReactNode;
}) {
  return (
    <label className="block">
      <span className="text-[13px] font-medium">{label}</span>
      {hint && <span className="mt-0.5 block text-xs leading-snug text-[var(--color-muted)]">{hint}</span>}
      <div className="mt-1.5">{children}</div>
      {error && <span className="mt-1 block text-xs text-[var(--color-danger)]">{error}</span>}
    </label>
  );
}

/**
 * Width is deliberately not part of this.
 *
 * `${input} w-auto` looked like it worked and did not: both are width utilities of equal
 * specificity, so which one wins is decided by Tailwind's own ordering in the stylesheet,
 * not by the order they appear in the string. A select meant to be as wide as "DE" was
 * stretching across a whole toolbar.
 */
const fieldBase =
  'rounded-[3px] border border-[var(--color-line)] bg-[var(--color-surface)] px-2.5 py-1.5 ' +
  'text-[13px] outline-none transition-colors placeholder:text-[var(--color-muted)]/60 ' +
  'focus:border-[var(--color-brand)] focus:ring-1 focus:ring-[var(--color-brand)]/30 ' +
  // Button carries the same note for the same reason: a disabled field that looks
  // identical to a live one does not read as "switched off", it reads as broken. The
  // button style select on the banner editor is locked whenever the two decision buttons
  // must match, and looked exactly like the ones above and below it that still worked.
  'disabled:cursor-not-allowed disabled:bg-[var(--color-sunken)] ' +
  'disabled:text-[var(--color-muted)]/70';

export const input = `w-full ${fieldBase}`;
/** Sized to its content; add `w-full` where a select should fill its field. */
export const select = fieldBase;

/**
 * A panel, not a floating card. Hairline rule and a flat surface — the drop shadow on
 * everything is most of what makes a generic admin look generic.
 */
export function Card({ title, count, actions, children }: {
  title?: string;
  /** Kept apart from the title so "(0)" is grey rather than part of the name. */
  count?: number;
  actions?: ReactNode;
  children: ReactNode;
}) {
  return (
    <section className="rounded-[4px] border border-[var(--color-line)] bg-[var(--color-surface)]">
      {(title || actions) && (
        // The title used to be 10px uppercase grey — the styling of a disabled table
        // header. Every card therefore looked equally unimportant, which is the same as
        // having no hierarchy at all.
        <header className="flex min-h-[38px] flex-wrap items-center justify-between gap-x-4 gap-y-1
                           border-b border-[var(--color-line)] px-4 py-2">
          <h2 className="text-[13px] font-semibold tracking-[-0.01em]">
            {title}
            {count !== undefined && (
              <span className="ml-1.5 font-normal text-[var(--color-muted)]">{count}</span>
            )}
          </h2>
          {actions}
        </header>
      )}
      <div className="p-4">{children}</div>
    </section>
  );
}

/** Tinted from the palette rather than from Tailwind's stock red/green/blue. */
export function Banner({ kind, children }: { kind: 'error' | 'ok' | 'info'; children: ReactNode }) {
  const styles = {
    error: 'border-[var(--color-danger)]/25 bg-[var(--color-danger)]/6 text-[var(--color-danger)]',
    ok: 'border-[var(--color-live)]/25 bg-[var(--color-live)]/6 text-[var(--color-live)]',
    info: 'border-[var(--color-line)] bg-[var(--color-sunken)] text-[var(--color-ink)]',
  }[kind];
  return (
    <div
      // Screen readers otherwise get no notice at all: every error in this console used to
      // be a purely visual event.
      role={kind === 'error' ? 'alert' : 'status'}
      className={`flex gap-2 rounded-[3px] border-l-2 border-y border-r px-3 py-2 text-sm ${styles}`}
    >
      {children}
    </div>
  );
}

/**
 * The wordmark: a square being ticked. Inline so it costs no request.
 *
 * Filled rather than outlined, because an outlined square with a tick in it is not a
 * wordmark, it is a checkbox — and next to 13px text in a toolbar it read as a form
 * control someone had already ticked.
 */
export function Mark({ size = 18 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 20 20" aria-hidden="true" className="shrink-0">
      <rect x="0" y="0" width="20" height="20" rx="4.5" fill="var(--color-brand)" />
      <path d="M5.2 10.3l3.2 3.2 6.4-6.9" fill="none" stroke="var(--color-surface)"
        strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

/**
 * A skeleton, not a spinner.
 *
 * The screens here fill in from several queries at once; a spinner per card makes the page
 * flash, while a block of the right size keeps the layout still and shows where content
 * will land.
 */
export function Skeleton({ rows = 3, className = '' }: { rows?: number; className?: string }) {
  return (
    <div className={`space-y-2 ${className}`} aria-hidden="true">
      {Array.from({ length: rows }, (_, i) => (
        <div key={i} className="h-4 rounded-[2px] bg-[var(--color-sunken)]"
          style={{ width: `${100 - (i % 3) * 18}%` }} />
      ))}
    </div>
  );
}

/**
 * What to show when a query failed.
 *
 * Its own component because the mistake it prevents is specific and severe: rendering an
 * error as emptiness. On a compliance console "0 blockierte Anbieter" and "konnte nicht
 * geladen werden" look identical to a user and mean opposite things — the first is a
 * statement about their site, the second about ours.
 */
export function LoadError({ what, onRetry }: { what: string; onRetry?: () => void }) {
  return (
    <div className="flex flex-wrap items-center gap-3 rounded-[3px] border-l-2 border-y border-r
                    border-[var(--color-danger)]/25 bg-[var(--color-danger)]/6 px-3 py-2
                    text-sm text-[var(--color-danger)]" role="alert">
      <span>{t('{what} konnte nicht geladen werden.', { what })}</span>
      {onRetry && (
        <button className="underline underline-offset-2" onClick={onRetry}>
          {t('Erneut versuchen')}
        </button>
      )}
    </div>
  );
}

/**
 * An empty state that says what to do next.
 *
 * The action is optional on purpose: a read-only role must not be told to press a button
 * it cannot see.
 */
export function Empty({ title, hint, action }: { title: string; hint?: string; action?: ReactNode }) {
  return (
    <div className="rounded-[3px] border border-dashed border-[var(--color-line)] px-4 py-8 text-center">
      <p className="text-sm font-medium">{title}</p>
      {hint && <p className="mx-auto mt-1 max-w-md text-xs text-[var(--color-muted)]">{hint}</p>}
      {action && <div className="mt-3 flex justify-center">{action}</div>}
    </div>
  );
}

/** Feedback that something worked. Disappears on its own; nothing here is worth a click. */
export function Toast({ message, onDone }: { message: string; onDone: () => void }) {
  useEffect(() => {
    const t = setTimeout(onDone, 4000);
    return () => clearTimeout(t);
  }, [message, onDone]);
  return (
    <div
      role="status"
      className="fixed bottom-5 left-1/2 z-50 -translate-x-1/2 rounded-[4px] border
                 border-[var(--color-line)] bg-[var(--color-ink)] px-4 py-2 text-[13px]
                 text-[var(--color-surface)] shadow-lg"
    >
      {message}
    </div>
  );
}

/**
 * Copy, and say whether it worked.
 *
 * navigator.clipboard rejects silently outside a secure context, which is exactly the
 * situation here — a console reached over plain http on a LAN address. Without the
 * fallback and the confirmation, a user can believe they have copied an API key that is
 * shown once and never again.
 */
export function CopyButton({ value, label }: { value: string; label?: string }) {
  const [state, setState] = useState<'idle' | 'ok' | 'fail'>('idle');

  useEffect(() => {
    if (state === 'idle') return;
    const t = setTimeout(() => setState('idle'), 2500);
    return () => clearTimeout(t);
  }, [state]);

  async function copy() {
    try {
      if (navigator.clipboard && window.isSecureContext) {
        await navigator.clipboard.writeText(value);
      } else {
        // execCommand is deprecated and still the only thing that works on http.
        const ta = document.createElement('textarea');
        ta.value = value;
        ta.style.position = 'fixed';
        ta.style.opacity = '0';
        document.body.appendChild(ta);
        ta.select();
        const ok = document.execCommand('copy');
        ta.remove();
        if (!ok) throw new Error('copy refused');
      }
      setState('ok');
    } catch {
      setState('fail');
    }
  }

  return (
    <button
      onClick={() => void copy()}
      className="rounded-[3px] border border-[var(--color-line)] px-2 py-1 text-xs
                 transition-colors hover:bg-[var(--color-sunken)]"
    >
      {state === 'ok' ? t('Kopiert ✓')
        : state === 'fail' ? t('Bitte von Hand markieren')
        : label ?? t('Kopieren')}
    </button>
  );
}
